import type { Gate, Question, QuizData } from '../types';
import type { VariantId } from '../analytics/variant';
import { trackVariantsError } from '../analytics/events';

/**
 * Apply a variant override to a base quiz config.
 *
 * v1 overridable surface (the only keys the resolver merges):
 *   - `title`       — wholesale replacement (must be a non-empty string)
 *   - `sub_title`   — wholesale replacement (string, may be empty)
 *   - `questions`   — wholesale array replacement (must be non-empty array of
 *                     well-formed Question objects; see isValidQuestion)
 *   - `gate`        — partial merge: only fields with type-correct values
 *                     replace base fields (booleans must be boolean, strings
 *                     must be string).
 *
 * Any other key on the variant override (e.g. `custom_colors`, `url_logic`) is
 * silently ignored in v1 — that's the v1/v2 boundary. v2 expands this surface
 * to UI/UX overrides (colors, typography, gate features). Adding keys here
 * without thinking about the v2 schema lock will paint v2 into a corner;
 * coordinate with the followup design doc before extending.
 *
 * **url_logic coupling invariant (v1)**: variants override `questions` but NOT
 * `url_logic`. Redirect resolution at the end of the quiz matches user-clicked
 * answer values against `base.url_logic.answers[].value`. Any variant whose
 * questions use different answer VALUES will silently fall back to the
 * default URL. v1 contract: answer values in `variants.<X>.questions[].answers`
 * MUST be a subset of (or identical to) the answer values used by
 * `base.url_logic`. v2 will add `url_logic` to the overridable surface to
 * relax this. See TODOS.md.
 *
 * Identity guarantee — when this function takes a "no change" path (control
 * bucket, missing variants block, missing variant entry, or any thrown error),
 * it returns the EXACT same object reference as `base`. Tests lean on `===`
 * to enforce that "no variant" can never accidentally produce a new object
 * that other sites' bundles might mishandle.
 *
 * Edge case: an override that is present but contains NO v1-overridable keys
 * (e.g. `{ B: { custom_colors: ... } }` — only v2 keys) returns a shallow
 * clone of base, not the base reference. The v1/v2 boundary regression test
 * (case 8) pins this. Acceptable because callers compare on field values,
 * not object identity, in this path.
 *
 * Failure mode — a malformed `variants` block (partial DDB write, schema
 * drift) MUST never break the site. Variant lookup AND merge are both wrapped
 * in try/catch (defends against Proxy traps or throwing accessors on
 * `base.variants`):
 *   1. console.error with structured prefix so it's grep-able in browser logs
 *   2. fire `variants_error` analytics event (truncated message) so ClickHouse
 *      can alert on it within 24h instead of relying on silent failure
 *   3. return base unchanged
 *
 * Ordering invariant — this function relies on `variantId` already being the
 * real bucket (derived from `pblk_sid` AFTER `waitForExternalSessionCookie`
 * has resolved). Calling it before the external session cookie arrives locks
 * the variant cache to `'control'` for the lifetime of the session and
 * silently disables B rendering. The store's initializeQuizData enforces this
 * structurally via Promise.all + the call order. The unit test (case 10 in
 * variants.spec.ts) pins applyVariant's BEHAVIOR with a stale sid (returns
 * base on 'control'); the call-order enforcement itself is structural — a
 * Pinia store integration test would catch a future refactor that breaks it.
 * See TODOS.md for the followup.
 */
function isValidQuestion(q: unknown): q is Question {
  if (!q || typeof q !== 'object') return false;
  const obj = q as Record<string, unknown>;
  if (typeof obj.id !== 'string' || obj.id.length === 0) return false;
  if (typeof obj.title !== 'string') return false;
  if (!Array.isArray(obj.answers) || obj.answers.length === 0) return false;
  // Each answer is a tuple: [id, text, icon?, color?, skip?]. Components index
  // answer[0] (id) and answer[1] (text) directly, so at minimum both must exist.
  return obj.answers.every((a) => Array.isArray(a) && a.length >= 2 && typeof a[0] === 'string');
}

function mergeGate(base: Gate, override: Partial<Gate>): Gate {
  const out: Gate = { ...base };
  for (const key of Object.keys(override) as (keyof Gate)[]) {
    const value = override[key];
    // Skip null/undefined explicitly (a partial DDB write of `useEmail: null`
    // would hide the email field otherwise).
    if (value === null || value === undefined) continue;
    // Type must match the base's type at that field. Guards against a
    // stringified "false" being accepted as a boolean usePhone, etc.
    if (typeof base[key] === typeof value) {
      (out as Record<keyof Gate, unknown>)[key] = value;
    }
  }
  return out;
}

export function applyVariant(base: QuizData, variantId: VariantId): QuizData {
  if (variantId !== 'A' && variantId !== 'B') return base;

  try {
    const override = base.variants?.[variantId];
    if (!override) return base;
    const resolved: QuizData = { ...base };
    if (typeof override.title === 'string' && override.title.length > 0) {
      resolved.title = override.title;
    }
    if (typeof override.sub_title === 'string') {
      resolved.sub_title = override.sub_title;
    }
    if (Array.isArray(override.questions)) {
      // An empty array would brick the quiz (allQuestionsAnswered would never
      // be satisfiable). A malformed entry would crash Question.vue's answer
      // tuple destructuring. Reject both at the boundary and observe via
      // variants_error so a future bad DDB write is loud, not silent.
      if (override.questions.length === 0 || !override.questions.every(isValidQuestion)) {
        throw new Error(
          override.questions.length === 0
            ? 'override.questions is empty; falling back to base questions'
            : 'override.questions contains malformed entries; falling back to base questions',
        );
      }
      resolved.questions = override.questions;
    }
    if (override.gate && typeof override.gate === 'object') {
      resolved.gate = mergeGate(base.gate, override.gate);
    }
    // v2 surface (DEV-1135, the deliberate schema-lock extension coordinated
    // with publinker#628): a variant may swap the rendering template + flow.
    // Only shape-checked here — full flow validation (invariants, template
    // registry) happens downstream in the store's _resolveFlowConfig, whose
    // fallback posture (legacy render + flow_error) contains a bad value the
    // same way this function contains bad copy overrides. An empty flow array
    // is rejected HERE because downstream treats `flow: []` as "wants flow"
    // and would burn a flow_error on every session (brick-class guard, same
    // rationale as empty questions above).
    if (typeof override.template_id === 'string' && override.template_id.length > 0) {
      resolved.template_id = override.template_id;
    }
    if (Array.isArray(override.flow)) {
      if (override.flow.length === 0) {
        throw new Error('override.flow is empty; falling back to base flow');
      }
      resolved.flow = override.flow;
    }
    return resolved;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[variants] applyVariant failed; falling back to base', {
      quiz_id: base.quiz_id,
      variant_id: variantId,
      error: message,
    });
    try {
      trackVariantsError({
        quiz_id: base.quiz_id,
        error_message: message,
      });
    } catch {
      // Observability is best-effort. Never let the error path itself crash.
    }
    return base;
  }
}
